import { useCallback, useState } from "react";

import { api } from "../api.js";
import { GameMinutesShareCard } from "../components/dashboard/GameMinutesShareCard.js";
import { PaidSessionExplorerCard } from "../components/dashboard/PaidSessionExplorerCard.js";
import { PayersCard } from "../components/dashboard/PayersCard.js";
import { PaymentFlowFunnelCard } from "../components/dashboard/PaymentFlowFunnelCard.js";
import { UptimeByDayCard } from "../components/dashboard/UptimeByDayCard.js";
import type { ChartTableSelection } from "../lib/chartTables.js";
import {
  formatEventTimeLocal,
  formatFilterRangeLabel,
  formatJpnRangeStartSuffix,
} from "../lib/dashboardTime.js";
import { useSharedChartTables } from "../lib/useSharedChartTables.js";
import type {
  DashboardTabHandler,
  FilterState,
  OpenRawEventsHandler,
  OverviewData,
} from "../types.js";

interface OverviewPanelProps {
  data: OverviewData | null;
  filters: FilterState;
  reportingTimezone: string;
  onOpenRawEvents: OpenRawEventsHandler;
  onOpenSessions: DashboardTabHandler;
  chartTables: ChartTableSelection;
  onChartTablesChange: (next: ChartTableSelection) => void;
  chartFullWeeks: boolean;
  onChartFullWeeksChange: (next: boolean) => void;
}

const OVERVIEW_PAID_SESSIONS_PDF_LIMIT = 200;

function OverviewChartBlocks({
  data,
  filters,
  reportingTimezone,
  onOpenRawEvents,
  onOpenSessions,
  uptimeWrapperClassName,
  gridClassName,
  forPrint,
  uptimeSelectedDeviceIds,
  onUptimeSelectedDeviceIdsChange,
  uptimeFullWeeks,
  onUptimeFullWeeksChange,
}: {
  data: OverviewData;
  filters: FilterState;
  reportingTimezone: string;
  onOpenRawEvents: OpenRawEventsHandler;
  onOpenSessions: DashboardTabHandler;
  uptimeWrapperClassName?: string;
  gridClassName: string;
  forPrint?: boolean;
  uptimeSelectedDeviceIds: ReadonlySet<string>;
  onUptimeSelectedDeviceIdsChange: (next: Set<string>) => void;
  uptimeFullWeeks: boolean;
  onUptimeFullWeeksChange: (next: boolean) => void;
}) {
  return (
    <div className={gridClassName}>
      <div className={uptimeWrapperClassName}>
        <UptimeByDayCard
          dailyUptimeByDevice={data.health.dailyUptimeByDevice}
          dailyUptime={data.health.dailyUptime}
          lookbackDailyUptime={data.health.lookbackDailyUptime}
          lookbackDailyUptimeByDevice={data.health.lookbackDailyUptimeByDevice}
          devices={data.health.devices}
          filters={filters}
          showCriteria={false}
          selectedDeviceIds={uptimeSelectedDeviceIds}
          onSelectedDeviceIdsChange={onUptimeSelectedDeviceIdsChange}
          hideTablePicker={forPrint}
          externallyReconciled
          fullWeeks={uptimeFullWeeks}
          onFullWeeksChange={onUptimeFullWeeksChange}
          hideWeekToggle={forPrint}
        />
      </div>
      <GameMinutesShareCard
        byGameMinutesPct={data.engagement.byGameMinutesPct}
        totalPlayMinutes={data.engagement.totalPlayMinutes}
        onOpenRawEvents={onOpenRawEvents}
        showCriteria={false}
      />
      <PaymentFlowFunnelCard
        funnelDisplay={data.retention.funnelDisplay}
        grossRevPence={data.retention.grossRevPence}
        registeredPayments={data.retention.registeredPayments}
        unregisteredPayments={data.retention.unregisteredPayments}
        unregisteredRevPence={data.retention.unregisteredRevPence}
        mobilePageviewEvents={data.retention.mobilePageviewEvents}
        paymentAttemptedEvents={data.retention.paymentAttemptedEvents}
        filters={filters}
        reportingTimezone={reportingTimezone}
        onOpenRawEvents={onOpenRawEvents}
        onOpenSessions={onOpenSessions}
        compact
        showCriteria={false}
      />
      <PayersCard
        data={data.retention}
        onOpenRawEvents={onOpenRawEvents}
        showCriteria={false}
        compact
      />
      <div className="min-w-0 lg:col-span-2">
        <PaidSessionExplorerCard
          filters={filters}
          reportingTimezone={reportingTimezone}
          onOpenRawEvents={onOpenRawEvents}
          showCriteria={false}
          showColumnInfo={false}
          pageSize={forPrint ? OVERVIEW_PAID_SESSIONS_PDF_LIMIT : undefined}
          hidePagination={forPrint}
        />
      </div>
    </div>
  );
}

export function OverviewPanel({
  data,
  filters,
  reportingTimezone,
  onOpenRawEvents,
  onOpenSessions,
  chartTables,
  onChartTablesChange,
  chartFullWeeks,
  onChartFullWeeksChange,
}: OverviewPanelProps) {
  const rangeLabel = formatFilterRangeLabel(filters.from, filters.to, reportingTimezone);
  const rangeStartJpn = formatJpnRangeStartSuffix(filters.from, reportingTimezone);
  const exportedAt = formatEventTimeLocal(new Date(), reportingTimezone);
  const [csvExporting, setCsvExporting] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);

  const tables = useSharedChartTables(
    data?.health.devices ?? [],
    chartTables,
    onChartTablesChange,
    filters,
  );
  const handleExportPdf = useCallback(() => {
    const prevTitle = document.title;
    document.title = `TeamTable Analytics Overview${rangeStartJpn}`;
    const restoreTitle = () => {
      document.title = prevTitle;
      window.removeEventListener("afterprint", restoreTitle);
    };
    window.addEventListener("afterprint", restoreTitle);
    window.print();
  }, [rangeStartJpn]);

  const handleExportCsv = useCallback(async () => {
    setCsvError(null);
    setCsvExporting(true);
    try {
      await api.downloadVenuePaymentsByDayCsv(filters);
    } catch (err) {
      setCsvError(err instanceof Error ? err.message : "CSV export failed");
    } finally {
      setCsvExporting(false);
    }
  }, [filters]);

  if (!data) {
    return <p className="py-5 text-[13px] text-text-secondary">No data available</p>;
  }

  return (
    <div id="overview-print-area" className="overview-print-area">
      <div className="print-only mb-4 border-b border-border-subtle pb-3">
        <h2 className="m-0 text-lg font-bold text-text-primary">
          TeamTable Analytics — Overview{rangeStartJpn}
        </h2>
        <p className="mt-1 mb-0 text-[12px] text-text-secondary">
          Range (local): {rangeLabel} · Reporting: {reportingTimezone}
        </p>
        <p className="mt-1 mb-0 text-[11px] text-text-secondary">Exported {exportedAt}</p>
      </div>

      <div className="no-print mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="m-0 text-[13px] text-text-secondary">
          Snapshot of key charts from Health, Retention, and Engagement — uptime by day, payment
          funnel, payers, paid sessions, and game minutes. Use{" "}
          <strong className="font-semibold text-text-primary">Export PDF</strong> for a printable
          copy (browser print dialog → Save as PDF), or{" "}
          <strong className="font-semibold text-text-primary">Export payments CSV</strong> for table
          × venue × day revenue (£). Uptime by day table selection and the full-weeks toggle are
          included in the PDF.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="rounded-lg border border-[#4f80c7] bg-[#1f3151] px-3 py-2 text-sm font-semibold text-white hover:bg-[#264278]"
            onClick={handleExportPdf}
          >
            Export PDF
          </button>
          <button
            type="button"
            className="rounded-lg border border-[#4f80c7] bg-[#1f3151] px-3 py-2 text-sm font-semibold text-white hover:bg-[#264278] disabled:cursor-not-allowed disabled:opacity-60"
            onClick={() => void handleExportCsv()}
            disabled={csvExporting}
            title="Table + Venue rows × reporting days; cells = effective payment_confirmed amounts in £ (same as Gross Rev.)"
          >
            {csvExporting ? "Exporting…" : "Export payments CSV"}
          </button>
        </div>
      </div>
      {csvError ? (
        <p className="no-print mb-3 mt-0 text-[12px] text-red-400" role="alert">
          {csvError}
        </p>
      ) : null}

      {/* Web: charts in a responsive grid */}
      <OverviewChartBlocks
        data={data}
        filters={filters}
        reportingTimezone={reportingTimezone}
        onOpenRawEvents={onOpenRawEvents}
        onOpenSessions={onOpenSessions}
        gridClassName="no-print grid grid-cols-1 gap-[10px] lg:grid-cols-2"
        uptimeWrapperClassName="min-w-0"
        uptimeSelectedDeviceIds={tables.selectedIds}
        onUptimeSelectedDeviceIdsChange={tables.onSelectedIdsChange}
        uptimeFullWeeks={chartFullWeeks}
        onUptimeFullWeeksChange={onChartFullWeeksChange}
      />

      {/* PDF / print: one chart per row — same uptime table selection as on screen */}
      <OverviewChartBlocks
        data={data}
        filters={filters}
        reportingTimezone={reportingTimezone}
        onOpenRawEvents={onOpenRawEvents}
        onOpenSessions={onOpenSessions}
        gridClassName="print-only overview-print-grid grid grid-cols-1 gap-[10px]"
        uptimeWrapperClassName="min-w-0"
        forPrint
        uptimeSelectedDeviceIds={tables.selectedIds}
        onUptimeSelectedDeviceIdsChange={tables.onSelectedIdsChange}
        uptimeFullWeeks={chartFullWeeks}
        onUptimeFullWeeksChange={onChartFullWeeksChange}
      />
    </div>
  );
}
